import React, { FC, useCallback } from 'react';

import { Chip } from '@mui/material';

import { ReactComponent as PostedIcon } from '@pharmaplan/common/assets/icons/postedIcon.svg';
import { ReactComponent as PendingIcon } from '@pharmaplan/common/assets/icons/pendingIcon.svg';
import {
  ICalendarEventType,
  PharmacistLegendVariant,
  ProfessionalReplacementTypes,
  ReplacementLabelsStyled,
  ReportRequestTypes,
} from '@pharmaplan/common';
import { ReactComponent as ExclamationIcon } from '../../../assets/svg/exclamationIcon.svg';
import { ChipType } from '../../../helpers/Constants';

import classes from './styles';

interface ICustomChip {
  type: ICalendarEventType | ChipType | ReportRequestTypes;
  label?: string | number;
  replacementType?: ProfessionalReplacementTypes;
}

const CustomChip: FC<ICustomChip> = ({ type, label, replacementType }) => {
  const isHourlyRateChip = type === ChipType.hourlyRate
    || type === ChipType.pharmacyHourlyRate
    || type === ChipType.replacementType || type === ChipType.promoBooking;

  const iconStyle = useCallback(() => {
    switch (type) {
      case ChipType.replacementType:
        return ReplacementLabelsStyled[
          replacementType ?? ProfessionalReplacementTypes.pharmacist
        ];
      case ChipType.promoBooking:
        return classes.promoBooking;
      case ChipType.notAvailable:
        return classes.notAvailable;
      case PharmacistLegendVariant.workshift:
        return classes.posted;
      case PharmacistLegendVariant.requested:
        return classes.requested;
      case PharmacistLegendVariant.booked:
      case ReportRequestTypes.payment:
        return classes.booked;
      case ChipType.hourlyRate:
        return classes.hourlyRate;
      case ChipType.pharmacyHourlyRate:
        return classes.pharmacyHourlyRate;
      default:
        return classes.posted;
    }
  }, [replacementType, type]);

  const customPosted = useCallback(
    () =>
      (
        <PostedIcon
          stroke={iconStyle().color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ),
    [iconStyle],
  );

  const icon = useCallback(() => {
    switch (type) {
      case ChipType.notAvailable:
        return <ExclamationIcon />;
      case PharmacistLegendVariant.booked:
      case PharmacistLegendVariant.workshift:
        return customPosted();
      case PharmacistLegendVariant.requested:
        return <PendingIcon />;
      default:
        return customPosted();
    }
  }, [type, customPosted]);

  const chipConfig = isHourlyRateChip
    ? {
      sx: classes.hourlyRateChip,
    }
    : {
      icon: icon(),
      sx: classes.chip,
    };

  return (
    <Chip
      label={label}
      sx={[iconStyle(), chipConfig.sx]}
      icon={chipConfig.icon}
    />
  );
};

export default CustomChip;
